import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import api, { getAll } from '../../services/api';
import { notify } from '../../utils/notifications';
import authStore from '../../store/authStore';
import { puoModificare } from '../../utils/permessi';
import { STATI, euro } from './Preventivi';
import {
  ArrowLeft, Plus, Trash2, Save, FileDown, Send, X, Check,
  AlertTriangle, Loader2, UserPlus
} from 'lucide-react';

const vuoto = {
  committente: '',
  oggetto: '',
  localita_partenza: '',
  localita_destinazione: '',
  tipo_viaggio: 'ANDATA_RITORNO',
  data_partenza: '',
  data_rientro: '',
  orario_partenza: '',
  passeggeri: '',
  mezzo_proposto: '',
  aliquota_iva: '10.00',
  valido_fino: '',
  condizioni: 'Pagamento: 30% alla conferma, saldo a fine servizio.\nSono inclusi carburante, pedaggi e vitto/alloggio autista.',
  note: '',
  righe: [{ descrizione: '', quantita: '1', prezzo_unitario: '' }],
};

const numerico = (v) => (v === '' || v === null || v === undefined ? null : v);

const PreventivoDettaglio = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { utente } = authStore();
  const modificabile = puoModificare(utente);
  const nuovo = id === 'nuovo';

  const [dati, setDati] = useState(vuoto);
  const [preventivo, setPreventivo] = useState(null);
  const [committenti, setCommittenti] = useState([]);
  const [mezzi, setMezzi] = useState([]);
  const [loading, setLoading] = useState(!nuovo);
  const [salvataggio, setSalvataggio] = useState(false);
  const [modaleInvio, setModaleInvio] = useState(false);
  const [modaleCommittente, setModaleCommittente] = useState(false);

  const carica = useCallback(async () => {
    try {
      const [listaCommittenti, listaMezzi] = await Promise.all([
        getAll('/preventivi/committenti/', { attivo: true }),
        getAll('/trasporti/mezzi/', { attivo: true }),
      ]);
      setCommittenti(listaCommittenti);
      setMezzi(listaMezzi);

      if (!nuovo) {
        const res = await api.get(`/preventivi/preventivi/${id}/`);
        setPreventivo(res.data);
        setDati({
          ...res.data,
          committente: res.data.committente || '',
          mezzo_proposto: res.data.mezzo_proposto || '',
          passeggeri: res.data.passeggeri ?? '',
          data_partenza: res.data.data_partenza || '',
          data_rientro: res.data.data_rientro || '',
          orario_partenza: res.data.orario_partenza ? res.data.orario_partenza.slice(0, 5) : '',
          valido_fino: res.data.valido_fino || '',
          righe: res.data.righe?.length
            ? res.data.righe.map((r) => ({
              descrizione: r.descrizione,
              quantita: String(r.quantita),
              prezzo_unitario: String(r.prezzo_unitario),
            }))
            : [{ descrizione: '', quantita: '1', prezzo_unitario: '' }],
        });
      }
    } catch (error) {
      console.error('Errore caricamento preventivo:', error);
      notify.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [id, nuovo]);

  useEffect(() => { carica(); }, [carica]);

  const cambia = (campo, valore) => setDati((d) => ({ ...d, [campo]: valore }));

  const cambiaRiga = (indice, campo, valore) => setDati((d) => ({
    ...d,
    righe: d.righe.map((r, i) => (i === indice ? { ...r, [campo]: valore } : r)),
  }));

  const aggiungiRiga = () => setDati((d) => ({
    ...d,
    righe: [...d.righe, { descrizione: '', quantita: '1', prezzo_unitario: '' }],
  }));

  const togliRiga = (indice) => setDati((d) => ({
    ...d,
    righe: d.righe.filter((_, i) => i !== indice),
  }));

  // I totali si ricalcolano mentre si scrive: l'operatore vede subito
  // dove arriva il preventivo, senza dover salvare.
  const imponibile = dati.righe.reduce(
    (a, r) => a + (Number(r.quantita) || 0) * (Number(r.prezzo_unitario) || 0), 0,
  );
  const iva = imponibile * (Number(dati.aliquota_iva) || 0) / 100;
  const totale = imponibile + iva;

  const salva = async () => {
    if (!dati.committente) {
      notify.error('Scegli il committente');
      return;
    }
    const righeValide = dati.righe.filter((r) => r.descrizione.trim());
    setSalvataggio(true);
    const toastId = notify.loading('Salvataggio...');
    try {
      const corpo = {
        committente: dati.committente,
        oggetto: dati.oggetto,
        localita_partenza: dati.localita_partenza,
        localita_destinazione: dati.localita_destinazione,
        tipo_viaggio: dati.tipo_viaggio,
        data_partenza: numerico(dati.data_partenza),
        data_rientro: numerico(dati.data_rientro),
        orario_partenza: numerico(dati.orario_partenza),
        passeggeri: numerico(dati.passeggeri),
        mezzo_proposto: numerico(dati.mezzo_proposto),
        aliquota_iva: dati.aliquota_iva || '0',
        valido_fino: numerico(dati.valido_fino),
        condizioni: dati.condizioni,
        note: dati.note,
        righe: righeValide.map((r, i) => ({
          descrizione: r.descrizione,
          quantita: r.quantita || '1',
          prezzo_unitario: r.prezzo_unitario || '0',
          ordine: i,
        })),
      };
      const res = nuovo
        ? await api.post('/preventivi/preventivi/', corpo)
        : await api.patch(`/preventivi/preventivi/${id}/`, corpo);
      notify.update(toastId, { type: 'success', render: `Preventivo ${res.data.numero} salvato` });
      if (nuovo) {
        navigate(`/admin/preventivi/${res.data.id}`, { replace: true });
      } else {
        setPreventivo(res.data);
      }
    } catch (error) {
      const d = error.response?.data;
      const messaggio = d
        ? Object.entries(d).map(([k, v]) => `${k}: ${Array.isArray(v) ? v[0] : v}`).join(' · ')
        : 'Errore nel salvataggio';
      notify.update(toastId, { type: 'error', render: messaggio });
    } finally {
      setSalvataggio(false);
    }
  };

  const scaricaPdf = async () => {
    const toastId = notify.loading('Preparazione PDF...');
    try {
      const res = await api.get(`/preventivi/preventivi/${id}/pdf/`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      // Da telefono `window.open` dopo una chiamata asincrona viene
      // bloccato come popup: il download va avviato da un elemento <a>,
      // che il browser non considera una finestra nuova.
      const a = document.createElement('a');
      if ('download' in a) {
        a.href = url;
        a.download = `Preventivo_${preventivo?.numero || id}.pdf`;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        // Browser molto vecchi: si apre nella stessa scheda
        window.location.href = url;
      }

      setTimeout(() => URL.revokeObjectURL(url), 60000);
      notify.update(toastId, { type: 'success', render: 'PDF scaricato' });
    } catch (error) {
      console.error('Errore PDF:', error);
      notify.update(toastId, { type: 'error', render: 'Errore nella generazione del PDF' });
    }
  };

  const cambiaStato = async (stato) => {
    const toastId = notify.loading('Aggiornamento...');
    try {
      const res = await api.post(`/preventivi/preventivi/${id}/cambia_stato/`, { stato });
      setPreventivo(res.data);
      notify.update(toastId, { type: 'success', render: `Segnato come ${STATI[stato].label.toLowerCase()}` });
    } catch (error) {
      notify.update(toastId, { type: 'error', render: 'Errore nel cambio di stato' });
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      </AdminLayout>
    );
  }

  const stato = STATI[preventivo?.stato] || STATI.BOZZA;
  const salvato = !nuovo && preventivo;

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 max-w-5xl">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate('/admin/preventivi')}
              className="p-2 hover:bg-gray-100 rounded-lg transition shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-gray-900 truncate">
                {nuovo ? 'Nuovo preventivo' : preventivo.numero}
              </h1>
              {salvato && (
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${stato.classe}`}>
                    {stato.label}
                  </span>
                  {preventivo.data_invio && (
                    <span className="text-xs text-gray-500">
                      inviato a {preventivo.inviato_a}
                    </span>
                  )}
                  {preventivo.scaduto && (
                    <span className="flex items-center gap-1 text-xs text-amber-700">
                      <AlertTriangle className="w-3 h-3" />
                      validità scaduta
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {salvato && (
              <button
                onClick={scaricaPdf}
                className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:border-gray-900 transition text-sm font-medium"
              >
                <FileDown className="w-4 h-4" />
                PDF
              </button>
            )}
            {salvato && modificabile && (
              <button
                onClick={() => setModaleInvio(true)}
                className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:border-gray-900 transition text-sm font-medium"
              >
                <Send className="w-4 h-4" />
                Invia
              </button>
            )}
            {modificabile && (
              <button
                onClick={salva}
                disabled={salvataggio}
                className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition text-sm font-semibold disabled:opacity-50"
              >
                {salvataggio ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Salva
              </button>
            )}
          </div>
        </div>

        {salvato && modificabile && preventivo.stato !== 'BOZZA' && (
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-500">Esito:</span>
            {['ACCETTATO', 'RIFIUTATO'].map((s) => (
              <button
                key={s}
                onClick={() => cambiaStato(s)}
                disabled={preventivo.stato === s}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition ${
                  preventivo.stato === s
                    ? `${STATI[s].classe} border-transparent`
                    : 'border-gray-300 text-gray-700 hover:border-gray-900'
                }`}
              >
                {s === 'ACCETTATO' ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                {STATI[s].label}
              </button>
            ))}
          </div>
        )}

        <fieldset disabled={!modificabile} className="space-y-4">
          {/* Committente */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-gray-900">Committente</h2>
              {modificabile && (
                <button
                  type="button"
                  onClick={() => setModaleCommittente(true)}
                  className="flex items-center gap-1.5 text-xs font-medium text-[#1b94ce] hover:underline"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  Nuovo committente
                </button>
              )}
            </div>
            <select
              value={dati.committente}
              onChange={(e) => cambia('committente', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
            >
              <option value="">— scegli —</option>
              {committenti.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.denominazione}{c.citta ? ` (${c.citta})` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Viaggio */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h2 className="text-sm font-bold text-gray-900 mb-3">Il viaggio</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Campo etichetta="Oggetto" span>
                <input type="text" value={dati.oggetto} onChange={(e) => cambia('oggetto', e.target.value)}
                  placeholder="Es. Gita scolastica a Pompei" className={INPUT} />
              </Campo>
              <Campo etichetta="Partenza da">
                <input type="text" value={dati.localita_partenza} onChange={(e) => cambia('localita_partenza', e.target.value)} className={INPUT} />
              </Campo>
              <Campo etichetta="Destinazione">
                <input type="text" value={dati.localita_destinazione} onChange={(e) => cambia('localita_destinazione', e.target.value)} className={INPUT} />
              </Campo>
              <Campo etichetta="Tipo di viaggio">
                <select value={dati.tipo_viaggio} onChange={(e) => cambia('tipo_viaggio', e.target.value)} className={INPUT}>
                  <option value="ANDATA_RITORNO">Andata e ritorno</option>
                  <option value="SOLA_ANDATA">Sola andata</option>
                  <option value="DISPOSIZIONE">Mezzo a disposizione</option>
                </select>
              </Campo>
              <Campo etichetta="Passeggeri">
                <input type="number" min="1" value={dati.passeggeri} onChange={(e) => cambia('passeggeri', e.target.value)} className={INPUT} />
              </Campo>
              <Campo etichetta="Data di partenza">
                <input type="date" value={dati.data_partenza} onChange={(e) => cambia('data_partenza', e.target.value)} className={INPUT} />
              </Campo>
              <Campo etichetta="Ora di partenza">
                <input type="time" value={dati.orario_partenza} onChange={(e) => cambia('orario_partenza', e.target.value)} className={INPUT} />
              </Campo>
              <Campo etichetta="Data di rientro">
                <input type="date" value={dati.data_rientro} onChange={(e) => cambia('data_rientro', e.target.value)} className={INPUT} />
              </Campo>
              <Campo etichetta="Mezzo proposto">
                <select value={dati.mezzo_proposto} onChange={(e) => cambia('mezzo_proposto', e.target.value)} className={INPUT}>
                  <option value="">— nessuno —</option>
                  {mezzi.map((m) => (
                    <option key={m.id} value={m.id}>{m.modello} · {m.capienza} posti</option>
                  ))}
                </select>
              </Campo>
            </div>
          </div>

          {/* Voci di costo */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-sm font-bold text-gray-900">Voci di costo</h2>
              {modificabile && (
                <button
                  type="button"
                  onClick={aggiungiRiga}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border border-gray-300 rounded-lg hover:border-gray-900 transition"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Aggiungi voce
                </button>
              )}
            </div>

            <div className="divide-y divide-gray-100">
              {dati.righe.map((riga, i) => {
                const importo = (Number(riga.quantita) || 0) * (Number(riga.prezzo_unitario) || 0);
                return (
                  <div key={i} className="px-4 py-3 flex flex-wrap items-end gap-2">
                    <div className="flex-1 min-w-[180px]">
                      <label className="block text-[11px] font-medium text-gray-500 mb-1">Descrizione</label>
                      <input type="text" value={riga.descrizione}
                        onChange={(e) => cambiaRiga(i, 'descrizione', e.target.value)}
                        placeholder="Es. Noleggio autobus 54 posti A/R" className={INPUT} />
                    </div>
                    <div className="w-20">
                      <label className="block text-[11px] font-medium text-gray-500 mb-1">Q.tà</label>
                      <input type="number" step="0.01" min="0" value={riga.quantita}
                        onChange={(e) => cambiaRiga(i, 'quantita', e.target.value)} className={INPUT} />
                    </div>
                    <div className="w-28">
                      <label className="block text-[11px] font-medium text-gray-500 mb-1">Prezzo €</label>
                      <input type="number" step="0.01" min="0" value={riga.prezzo_unitario}
                        onChange={(e) => cambiaRiga(i, 'prezzo_unitario', e.target.value)} className={INPUT} />
                    </div>
                    <div className="w-24 text-right">
                      <label className="block text-[11px] font-medium text-gray-500 mb-1">Importo</label>
                      <p className="py-2 text-sm font-semibold text-gray-900">{euro(importo)}</p>
                    </div>
                    {modificabile && dati.righe.length > 1 && (
                      <button
                        type="button"
                        onClick={() => togliRiga(i)}
                        className="p-2 mb-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
              <div className="ml-auto max-w-xs space-y-1.5">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Imponibile</span>
                  <span className="font-semibold text-gray-900">{euro(imponibile)}</span>
                </div>
                <div className="flex justify-between text-sm items-center">
                  <span className="text-gray-600 flex items-center gap-1.5">
                    IVA
                    <input type="number" step="0.01" min="0" max="100" value={dati.aliquota_iva}
                      onChange={(e) => cambia('aliquota_iva', e.target.value)}
                      className="w-16 px-1.5 py-0.5 border border-gray-300 rounded text-xs text-right" />
                    %
                  </span>
                  <span className="font-semibold text-gray-900">{euro(iva)}</span>
                </div>
                <div className="flex justify-between pt-1.5 border-t border-gray-300">
                  <span className="font-bold text-gray-900">Totale</span>
                  <span className="font-bold text-lg text-[#1b94ce]">{euro(totale)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Condizioni */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Campo etichetta="Valido fino al">
              <input type="date" value={dati.valido_fino} onChange={(e) => cambia('valido_fino', e.target.value)} className={INPUT} />
            </Campo>
            <div />
            <Campo etichetta="Condizioni (compaiono nel PDF)" span>
              <textarea rows={3} value={dati.condizioni} onChange={(e) => cambia('condizioni', e.target.value)} className={INPUT} />
            </Campo>
            <Campo etichetta="Note interne (non compaiono nel PDF)" span>
              <textarea rows={2} value={dati.note} onChange={(e) => cambia('note', e.target.value)} className={INPUT} />
            </Campo>
          </div>
        </fieldset>
      </div>

      {modaleInvio && (
        <ModaleInvio
          preventivo={preventivo}
          onClose={() => setModaleInvio(false)}
          onInviato={(p) => { setPreventivo(p); setModaleInvio(false); }}
        />
      )}
      {modaleCommittente && (
        <ModaleCommittente
          onClose={() => setModaleCommittente(false)}
          onCreato={(c) => {
            setCommittenti((v) => [...v, c].sort((a, b) => a.denominazione.localeCompare(b.denominazione)));
            cambia('committente', c.id);
            setModaleCommittente(false);
          }}
        />
      )}
    </AdminLayout>
  );
};

const INPUT = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1b94ce] disabled:bg-gray-50 disabled:text-gray-500';

const Campo = ({ etichetta, children, span }) => (
  <div className={span ? 'sm:col-span-2' : ''}>
    <label className="block text-[11px] font-medium text-gray-500 mb-1">{etichetta}</label>
    {children}
  </div>
);

const ModaleInvio = ({ preventivo, onClose, onInviato }) => {
  const [email, setEmail] = useState(preventivo.committente_email || '');
  const [messaggio, setMessaggio] = useState('');
  const [invio, setInvio] = useState(false);

  const invia = async (e) => {
    e.preventDefault();
    setInvio(true);
    const toastId = notify.loading('Invio in corso...');
    try {
      const res = await api.post(`/preventivi/preventivi/${preventivo.id}/invia/`, {
        email, messaggio,
      });
      notify.update(toastId, { type: 'success', render: `Inviato a ${res.data.inviato_a}` });
      onInviato(res.data.preventivo);
    } catch (error) {
      notify.update(toastId, {
        type: 'error',
        render: error.response?.data?.errore || 'Invio non riuscito',
      });
    } finally {
      setInvio(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
        <div className="border-b border-gray-200 px-4 py-3 flex justify-between items-center">
          <h2 className="text-base font-bold text-gray-900">Invia {preventivo.numero}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={invia} className="p-4 space-y-3">
          <p className="text-xs text-gray-500">
            Il preventivo viene allegato in PDF. Lo stato passa a “inviato”.
          </p>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Destinatario *</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="indirizzo@esempio.it" className={INPUT} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Messaggio</label>
            <textarea rows={4} value={messaggio} onChange={(e) => setMessaggio(e.target.value)}
              placeholder="Lascia vuoto per usare il testo standard" className={INPUT} />
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
            <button type="button" onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold hover:bg-gray-50">
              Annulla
            </button>
            <button type="submit" disabled={invio}
              className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-gray-800 disabled:opacity-50">
              {invio ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Invia
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ModaleCommittente = ({ onClose, onCreato }) => {
  const [dati, setDati] = useState({
    denominazione: '', tipo: 'SCUOLA', referente: '', email: '',
    telefono: '', citta: '', provincia: '', partita_iva: '',
  });
  const [salvataggio, setSalvataggio] = useState(false);

  const salva = async (e) => {
    e.preventDefault();
    setSalvataggio(true);
    const toastId = notify.loading('Salvataggio...');
    try {
      const res = await api.post('/preventivi/committenti/', dati);
      notify.update(toastId, { type: 'success', render: 'Committente creato' });
      onCreato(res.data);
    } catch (error) {
      const d = error.response?.data;
      notify.update(toastId, {
        type: 'error',
        render: d ? Object.entries(d).map(([k, v]) => `${k}: ${Array.isArray(v) ? v[0] : v}`).join(' · ') : 'Errore',
      });
    } finally {
      setSalvataggio(false);
    }
  };

  const c = (campo, valore) => setDati((d) => ({ ...d, [campo]: valore }));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b border-gray-200 px-4 py-3 flex justify-between items-center sticky top-0 bg-white">
          <h2 className="text-base font-bold text-gray-900">Nuovo committente</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={salva} className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Campo etichetta="Denominazione *" span>
            <input type="text" required value={dati.denominazione} onChange={(e) => c('denominazione', e.target.value)}
              placeholder="Scuola, azienda o nome e cognome" className={INPUT} />
          </Campo>
          <Campo etichetta="Tipo">
            <select value={dati.tipo} onChange={(e) => c('tipo', e.target.value)} className={INPUT}>
              <option value="SCUOLA">Scuola o istituto</option>
              <option value="AZIENDA">Azienda</option>
              <option value="ASSOCIAZIONE">Associazione</option>
              <option value="PARROCCHIA">Parrocchia</option>
              <option value="ENTE">Ente pubblico</option>
              <option value="PRIVATO">Privato</option>
              <option value="ALTRO">Altro</option>
            </select>
          </Campo>
          <Campo etichetta="Referente">
            <input type="text" value={dati.referente} onChange={(e) => c('referente', e.target.value)} className={INPUT} />
          </Campo>
          <Campo etichetta="Email">
            <input type="email" value={dati.email} onChange={(e) => c('email', e.target.value)} className={INPUT} />
          </Campo>
          <Campo etichetta="Telefono">
            <input type="text" value={dati.telefono} onChange={(e) => c('telefono', e.target.value)} className={INPUT} />
          </Campo>
          <Campo etichetta="Città">
            <input type="text" value={dati.citta} onChange={(e) => c('citta', e.target.value)} className={INPUT} />
          </Campo>
          <Campo etichetta="Provincia">
            <input type="text" maxLength={5} value={dati.provincia} onChange={(e) => c('provincia', e.target.value.toUpperCase())} className={INPUT} />
          </Campo>
          <Campo etichetta="Partita IVA" span>
            <input type="text" value={dati.partita_iva} onChange={(e) => c('partita_iva', e.target.value)} className={INPUT} />
          </Campo>
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2 border-t border-gray-200">
            <button type="button" onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold hover:bg-gray-50">
              Annulla
            </button>
            <button type="submit" disabled={salvataggio}
              className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-gray-800 disabled:opacity-50">
              {salvataggio ? 'Salvataggio...' : 'Crea'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PreventivoDettaglio;
